import { EmployeeGroupFragment } from 'generated/resources/graphql'

import {
  employeeGroupOptions,
  jobCatalogTitleOptions,
  jobTitleOptions,
  legalEntityOptions,
  locationOptions,
  teamOptions,
} from 'modules/core/components/MultiFilters/commonOptions'
import type { Value } from 'modules/core/components/MultiFilters/hooks/utils/defaultEvaluators'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { employeeJobTitle } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { employeeEmployeeGroup } from 'modules/employees/store/collections/employeeEmployeeGroup'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { employeeTeams } from 'modules/teams/store/employeeTeams'

import { employeeStatusOptions } from './commonOptions'

export const filterFields = async (
  employeeGroups: EmployeeGroupFragment[]
): Promise<FilterOption[]> => {
  return [
    {
      label: i18n.t('filters.multi_filters.employee_status'),
      value: 'employee_status',
      type: 'equal_choice',
      options: await employeeStatusOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.employee'),
      value: 'employee_name',
      type: 'string',
    },
    {
      label: i18n.t('filters.multi_filters.location'),
      value: 'location',
      type: 'single_choice',
      options: await locationOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.team'),
      value: 'team',
      type: 'single_choice',
      options: await teamOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.legal_entity'),
      value: 'legal_entity',
      type: 'single_choice',
      options: await legalEntityOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.job_title'),
      value: 'job_title',
      type: 'single_choice',
      options: await jobTitleOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.job_role'),
      value: 'job_role',
      type: 'single_choice',
      options: await jobCatalogTitleOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.employee_group'),
      value: 'employee_group',
      type: 'single_choice',
      options: employeeGroupOptions(employeeGroups),
    },
  ]
}

export const getFilterValue = (employee: Employee, value: Value) => {
  switch (value) {
    case 'employee_status':
      return employee

    case 'employee_name': {
      const userId = employee.attributes.access_id
      const user = users.get(userId)
      return user?.fullName()
    }

    case 'employee_group':
      return employeeEmployeeGroup(employee).id

    case 'location':
      return employee.attributes.location_id

    case 'team':
      return employeeTeams(teams, memberships, employee.id).map((team) => team.attributes.id)

    case 'legal_entity':
      return employeeLegalEntityId(employee)

    case 'job_title':
      return employeeJobTitle(contractVersions, employee)

    case 'job_role':
      return employeeJobRole(contractVersions, employee)?.attributes.name
  }
}
